export type PathSegment =
	| { kind: "key"; key: string }
	| { kind: "index"; index: number }
	| { kind: "anyIndex" }
	| { kind: "anyKey" }

const BARE_KEY = /^[A-Za-z_$][A-Za-z0-9_$-]*$/
const BANNED_KEYS = new Set(["__proto__", "prototype", "constructor"])

export const MAX_PATH_SEGMENTS = 8
export const MAX_WALK_DEPTH = 8
export const MAX_WALK_NODES = 1_000
export const MAX_INPUT_BYTES = 64_000

export function isSafeObjectKey(key: string): boolean {
	if (!key || BANNED_KEYS.has(key)) return false
	// A non-NFC or confusable key would compare unequal to the same-looking rule.
	return key.normalize("NFC") === key
}

interface Reader {
	source: string
	pos: number
}

function readQuotedKey(reader: Reader): string | null {
	let out = ""
	reader.pos += 1
	while (reader.pos < reader.source.length) {
		const char = reader.source[reader.pos]
		if (char === "\\") {
			const next = reader.source[reader.pos + 1]
			if (next === undefined) return null
			out += next
			reader.pos += 2
			continue
		}
		if (char === '"') {
			reader.pos += 1
			return out
		}
		out += char
		reader.pos += 1
	}
	return null
}

function readBracket(reader: Reader): PathSegment | null {
	reader.pos += 1
	const char = reader.source[reader.pos]
	if (char === undefined) return null
	if (char === "]") {
		reader.pos += 1
		return { kind: "anyIndex" }
	}
	if (char === '"') {
		const key = readQuotedKey(reader)
		if (key === null || !isSafeObjectKey(key)) return null
		if (reader.source[reader.pos] !== "]") return null
		reader.pos += 1
		return { kind: "key", key }
	}
	let digits = ""
	while (
		reader.pos < reader.source.length &&
		/[0-9]/.test(reader.source[reader.pos]!)
	) {
		digits += reader.source[reader.pos]
		reader.pos += 1
	}
	if (!digits || reader.source[reader.pos] !== "]") return null
	reader.pos += 1
	const index = Number(digits)
	if (!Number.isSafeInteger(index)) return null
	return { kind: "index", index }
}

function readBare(reader: Reader): PathSegment | null {
	let token = ""
	while (reader.pos < reader.source.length) {
		const char = reader.source[reader.pos]!
		if (char === "." || char === "[") break
		token += char
		reader.pos += 1
	}
	if (token === "*") return { kind: "anyKey" }
	if (!BARE_KEY.test(token) || !isSafeObjectKey(token)) return null
	return { kind: "key", key: token }
}

export function parseFieldPath(path: string): PathSegment[] | null {
	if (!path || path.length > 512) return null
	const reader: Reader = { source: path, pos: 0 }
	const segments: PathSegment[] = []

	while (reader.pos < path.length) {
		const segment =
			path[reader.pos] === "[" ? readBracket(reader) : readBare(reader)
		if (!segment) return null
		segments.push(segment)
		if (segments.length > MAX_PATH_SEGMENTS) return null

		if (reader.pos >= path.length) break
		if (path[reader.pos] === ".") {
			reader.pos += 1
			if (reader.pos >= path.length) return null
			continue
		}
		if (path[reader.pos] !== "[") return null
	}

	return segments.length > 0 ? segments : null
}

export function formatFieldPath(segments: PathSegment[]): string {
	return segments.reduce((acc, segment) => {
		switch (segment.kind) {
			case "anyIndex":
				return `${acc}[]`
			case "index":
				return `${acc}[${segment.index}]`
			case "anyKey":
				return acc ? `${acc}.*` : "*"
			case "key": {
				if (!BARE_KEY.test(segment.key)) {
					return `${acc}[${JSON.stringify(segment.key)}]`
				}
				return acc ? `${acc}.${segment.key}` : segment.key
			}
		}
	}, "")
}

function segmentsEqual(a: PathSegment, b: PathSegment): boolean {
	if (a.kind !== b.kind) return false
	if (a.kind === "key" && b.kind === "key") return a.key === b.key
	if (a.kind === "index" && b.kind === "index") return a.index === b.index
	return true
}

/** Segment-wise, so `metadata2.note` is not read as a child of `metadata`. */
export function isDescendantSegments(
	child: PathSegment[],
	parent: PathSegment[],
): boolean {
	if (child.length <= parent.length) return false
	return parent.every((segment, index) => segmentsEqual(segment, child[index]!))
}

export function isDescendantPath(
	childPath: string,
	parentPath: string,
): boolean {
	const child = parseFieldPath(childPath)
	const parent = parseFieldPath(parentPath)
	return !!child && !!parent && isDescendantSegments(child, parent)
}

export interface PathConflict {
	path: string
	/** The shallower path whose rule makes this one unreachable. */
	shadowedBy: string
}

/**
 * Paths a terminal ancestor already covers, keyed as the caller wrote them. A rule
 * that pins or drops a container decides its whole subtree, so anything below is dead.
 */
export function findShadowedPaths<R>(
	fields: Record<string, R>,
	terminal: (rule: R) => boolean,
): PathConflict[] {
	const parsed: { path: string; segments: PathSegment[]; rule: R }[] = []
	for (const [path, rule] of Object.entries(fields)) {
		const segments = parseFieldPath(path)
		if (segments) parsed.push({ path, segments, rule })
	}
	parsed.sort(
		(a, b) =>
			a.segments.length - b.segments.length || a.path.localeCompare(b.path),
	)

	const conflicts: PathConflict[] = []
	for (const entry of parsed) {
		const ancestor = parsed.find(
			(candidate) =>
				terminal(candidate.rule) &&
				isDescendantSegments(entry.segments, candidate.segments),
		)
		if (ancestor)
			conflicts.push({ path: entry.path, shadowedBy: ancestor.path })
	}
	return conflicts
}

export interface RuleNode<R> {
	rule: R | null
	path: string
	keys: Map<string, RuleNode<R>>
	indices: Map<number, RuleNode<R>>
	anyKey: RuleNode<R> | null
	anyIndex: RuleNode<R> | null
	/** Some descendant carries a rule, so this node is a legitimate ancestor. */
	hasRuledDescendant: boolean
}

function emptyNode<R>(path: string): RuleNode<R> {
	return {
		rule: null,
		path,
		keys: new Map(),
		indices: new Map(),
		anyKey: null,
		anyIndex: null,
		hasRuledDescendant: false,
	}
}

export type CompiledRules<R> =
	| {
			ok: true
			root: RuleNode<R>
			paths: { path: string; rule: R }[]
			/** Dropped because a terminal ancestor already covers them. */
			shadowed: string[]
	  }
	| { ok: false; path: string }

// Rules sort shortest-path-first then lexicographically so injection order, and
// therefore the idempotency key, is identical on every run.
export function compileRuleTrie<R>(
	fields: Record<string, R>,
	options: { terminal?: (rule: R) => boolean } = {},
): CompiledRules<R> {
	const root = emptyNode<R>("")
	const paths: {
		rawPath: string
		path: string
		rule: R
		segments: PathSegment[]
	}[] = []

	for (const [rawPath, rule] of Object.entries(fields)) {
		const segments = parseFieldPath(rawPath)
		if (!segments) return { ok: false, path: rawPath }
		paths.push({ rawPath, path: formatFieldPath(segments), rule, segments })
	}

	paths.sort(
		(a, b) =>
			a.segments.length - b.segments.length || a.path.localeCompare(b.path),
	)

	const skip = new Set<string>()
	if (options.terminal) {
		for (const conflict of findShadowedPaths(fields, options.terminal)) {
			skip.add(conflict.path)
		}
	}

	for (const entry of paths) {
		if (skip.has(entry.rawPath)) continue
		let node = root
		const walked: PathSegment[] = []
		for (const segment of entry.segments) {
			walked.push(segment)
			const childPath = formatFieldPath(walked)
			let child: RuleNode<R> | null
			switch (segment.kind) {
				case "key":
					child = node.keys.get(segment.key) ?? null
					if (!child) node.keys.set(segment.key, (child = emptyNode(childPath)))
					break
				case "index":
					child = node.indices.get(segment.index) ?? null
					if (!child)
						node.indices.set(segment.index, (child = emptyNode(childPath)))
					break
				case "anyKey":
					child = node.anyKey ?? (node.anyKey = emptyNode(childPath))
					break
				case "anyIndex":
					child = node.anyIndex ?? (node.anyIndex = emptyNode(childPath))
					break
			}
			node.hasRuledDescendant = true
			node = child
		}
		node.rule = entry.rule
	}

	return {
		ok: true,
		root,
		paths: paths
			.filter((entry) => !skip.has(entry.rawPath))
			.map(({ path, rule }) => ({ path, rule })),
		shadowed: [...skip],
	}
}

function childFor<R>(
	node: RuleNode<R>,
	key: string | number,
): RuleNode<R> | null {
	if (typeof key === "number") {
		return node.indices.get(key) ?? node.anyIndex ?? null
	}
	return node.keys.get(key) ?? node.anyKey ?? null
}

export interface WalkNode<R> {
	/** Concrete path into the input, e.g. `items[2].sku`. */
	path: string
	value: unknown
	rule: R | null
	/** No trie node matched: the model sent something no rule covers. */
	unmatched: boolean
	/** Matched a node with no rule of its own but with ruled descendants. */
	ancestor: boolean
	/** Some rule sits below this node, so its children still need checking. */
	hasRuledDescendant: boolean
}

export type WalkVisit = "recurse" | "stop"

export type WalkOutcome =
	| { ok: true }
	| { ok: false; reason: "too-deep" | "too-many-nodes" | "too-large" }

function isContainer(value: unknown): boolean {
	return typeof value === "object" && value !== null
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return isContainer(value) && !Array.isArray(value)
}

function appendKey(prefix: string, key: string): string {
	const segment = formatFieldPath([{ kind: "key", key }])
	if (!prefix) return segment
	return segment.startsWith("[")
		? `${prefix}${segment}`
		: `${prefix}.${segment}`
}

/**
 * Walks the model's input, not the schema, so an unlisted field is visible and
 * enforcement survives a tool whose schema we never cached.
 */
export function walkInput<R>(
	input: Record<string, unknown>,
	root: RuleNode<R>,
	visit: (node: WalkNode<R>) => WalkVisit,
): WalkOutcome {
	let serialized: string
	try {
		serialized = JSON.stringify(input) ?? ""
	} catch {
		return { ok: false, reason: "too-large" }
	}
	if (serialized.length > MAX_INPUT_BYTES) {
		return { ok: false, reason: "too-large" }
	}

	let budget = MAX_WALK_NODES
	let overflow: WalkOutcome | null = null

	const descend = (
		container: unknown,
		node: RuleNode<R> | null,
		prefix: string,
		depth: number,
	): void => {
		if (overflow) return
		if (depth > MAX_WALK_DEPTH) {
			overflow = { ok: false, reason: "too-deep" }
			return
		}

		const entries: [string | number, unknown][] = Array.isArray(container)
			? container.map((value, index) => [index, value])
			: Object.entries(container as Record<string, unknown>)

		for (const [key, value] of entries) {
			if (overflow) return
			if (budget-- <= 0) {
				overflow = { ok: false, reason: "too-many-nodes" }
				return
			}

			const child = node ? childFor(node, key) : null
			const path =
				typeof key === "number" ? `${prefix}[${key}]` : appendKey(prefix, key)

			const decision = visit({
				path,
				value,
				rule: child?.rule ?? null,
				unmatched: !child,
				ancestor: !!child && !child.rule,
				hasRuledDescendant: child?.hasRuledDescendant ?? false,
			})

			if (decision === "recurse" && isContainer(value)) {
				descend(value, child, path, depth + 1)
			}
		}
	}

	descend(input, root, "", 1)
	return overflow ?? { ok: true }
}

export function hasWildcardSegment(segments: PathSegment[]): boolean {
	return segments.some(
		(segment) => segment.kind === "anyIndex" || segment.kind === "anyKey",
	)
}

/** The value the input carries at a concrete path; undefined when absent. */
export function readAtPath(
	input: Record<string, unknown>,
	segments: PathSegment[],
): unknown {
	let current: unknown = input
	for (const segment of segments) {
		if (segment.kind === "key") {
			if (!isPlainObject(current)) return undefined
			if (!Object.prototype.hasOwnProperty.call(current, segment.key)) {
				return undefined
			}
			current = current[segment.key]
			continue
		}
		if (segment.kind === "index") {
			if (!Array.isArray(current) || current.length <= segment.index) {
				return undefined
			}
			current = current[segment.index]
			continue
		}
		// A wildcard names many sites, so no single value exists to read.
		return undefined
	}
	return current
}

export type InjectOutcome =
	| { ok: true; paths: string[] }
	| { ok: false; reason: "no-container" | "no-array" }

// A structured value would otherwise be the same reference in every wildcard slot
// and in both the guarded and the audited payload.
function cloneInjected(value: unknown): unknown {
	return typeof value === "object" && value !== null
		? structuredClone(value)
		: value
}

/**
 * Writes an injected value at a rule path. Wildcards only ever reach containers
 * the model actually sent; a missing array is a refusal because no index is implied.
 */
export function injectAtPath(
	target: Record<string, unknown>,
	segments: PathSegment[],
	value: unknown,
): InjectOutcome {
	const written: string[] = []

	const place = (
		container: unknown,
		index: number,
		prefix: PathSegment[],
	): InjectOutcome | null => {
		const segment = segments[index]!
		const last = index === segments.length - 1

		const step = (
			child: unknown,
			assign: (next: unknown) => void,
			childPrefix: PathSegment[],
		): InjectOutcome | null => {
			if (last) {
				assign(cloneInjected(value))
				written.push(formatFieldPath(childPrefix))
				return null
			}
			const nextSegment = segments[index + 1]!
			if (child === undefined) {
				// A wildcard over a container the model never sent has nothing to write.
				if (nextSegment.kind === "anyIndex" || nextSegment.kind === "anyKey") {
					return null
				}
				if (nextSegment.kind === "index") {
					return { ok: false, reason: "no-array" }
				}
				// Only an object can be conjured; an absent array leaves no index to write.
				const created: Record<string, unknown> = {}
				assign(created)
				return place(created, index + 1, childPrefix)
			}
			return place(child, index + 1, childPrefix)
		}

		switch (segment.kind) {
			case "key": {
				if (!isPlainObject(container))
					return { ok: false, reason: "no-container" }
				return step(
					Object.prototype.hasOwnProperty.call(container, segment.key)
						? container[segment.key]
						: undefined,
					(next) => {
						container[segment.key] = next
					},
					[...prefix, segment],
				)
			}
			case "index": {
				if (!Array.isArray(container) || container.length <= segment.index) {
					return { ok: false, reason: "no-array" }
				}
				return step(
					container[segment.index],
					(next) => {
						container[segment.index] = next
					},
					[...prefix, segment],
				)
			}
			case "anyIndex": {
				if (!Array.isArray(container)) return { ok: false, reason: "no-array" }
				for (let position = 0; position < container.length; position++) {
					const failure = step(
						container[position],
						(next) => {
							container[position] = next
						},
						[...prefix, { kind: "index", index: position }],
					)
					if (failure) return failure
				}
				return null
			}
			case "anyKey": {
				if (!isPlainObject(container))
					return { ok: false, reason: "no-container" }
				for (const key of Object.keys(container)) {
					const failure = step(
						container[key],
						(next) => {
							container[key] = next
						},
						[...prefix, { kind: "key", key }],
					)
					if (failure) return failure
				}
				return null
			}
		}
	}

	const failure = place(target, 0, [])
	return failure ?? { ok: true, paths: written }
}
